import { IAppContext } from 'App/AppContext';
import { ApprovalState } from 'models/approval';
import DateRange from 'models/dateRange';
import DateRangeDTO from 'models/dto/dateRangeDTO';
import IdListDTO from 'models/dto/IdListDTO';
import { TaskCheckListConversionDTO } from 'models/dto/tasks/taskChecklistConversionDTO';
import { TaskContextDTO } from 'models/dto/tasks/taskContextDTO';
import TaskDTO, { TaskStateDTO, TaskState_TranslationDTO } from 'models/dto/tasks/taskDTO';
import TaskLookupDTO from 'models/dto/tasks/taskLookupDTO';
import { TaskRelationDTO } from 'models/dto/tasks/taskRelationDTO';
import TasksDTO from 'models/dto/tasks/tasksDTO';
import TaskTemplateSceneDTO from 'models/dto/tasks/taskTemplateSceneDTO';
import Entity, { EntityTypes } from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import RecurringPattern from 'models/recurringPattern';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskCheckListConversion } from 'models/tasks/taskChecklistConversion';
import { TaskContext } from 'models/tasks/taskContext';
import { TaskCheckList, TaskState, TaskState_Translation } from 'models/tasks/taskHelperClasses';
import { TaskRelation } from 'models/tasks/taskRelation';
import Tasks from 'models/tasks/tasks';
import TaskTemplateScene, { TemplateStats } from 'models/tasks/taskTemplateScene';
import { SystemTaskTypes, TaskKinds } from 'models/tasks/taskType';
import {
  addDateTimeDays,
  fromApiDateOptional,
  fromApiDateTimeOptional,
  fromApiDateTimeRequired,
  toApiDateTimeOptional,
  toApiDateTimeRequired,
} from 'utils/datetime';
import { newGuidNil } from 'utils/guid';
import { mapFromApiErrorInfoDictionary } from './apiErrorInfoMapping';
import { mapFromAssets } from './assetMapping';
import { mapFromAuditTrail } from './auditMapping';
import { mapFromControls } from './controlMappings';
import { mapToKPIData } from './kpiMapping';
import { mapFromLanguage } from './langMapping';
import { mapFromObjectives } from './objectiveMapping';
import { mapFromProcessingActivities } from './processingActivityMapping';
import { mapFromProcesses } from './processMapping';
import { mapFromRisksArray } from './riskMapping';
import { mapRelationSourceIdForTaskSync, shouldIncludeRelationForNewTaskSync } from './taskRelationMappingHelpers';
import { mapFromTaskType } from './taskTypeMapping';
import { mapFromThemes } from './themeMapping';

export interface ITaskMappingOptions {
  includeTaskRelations?: boolean;
  originalTaskRelations?: TaskRelation[];
}

export function mapFromTaskLookupDTO(dto: TaskLookupDTO): Entity {
  return new Entity(dto.taskId, EntityTypes.Task, dto.name);
}

export function mapToTasks(tasks: Task[], options?: ITaskMappingOptions): TaskDTO[] {
  return tasks.map((task) => mapToTask(task, options));
}

export function mapToTask(task: Task, options?: ITaskMappingOptions): TaskDTO {
  const newTaskDTO: TaskDTO = new TaskDTO();

  newTaskDTO.taskId = task.taskId;
  newTaskDTO.authSchemaId = task.authSchemaId;
  newTaskDTO.taskStateId = task.taskStateId;
  newTaskDTO.sortOrder = task.sortOrder;
  newTaskDTO.creatorId = newGuidNil(); //creator and created are set by the back-end
  newTaskDTO.created = toApiDateTimeRequired(new Date());
  newTaskDTO.completed = toApiDateTimeOptional(task.completed);
  newTaskDTO.taskMasterId = task.taskMasterId;
  newTaskDTO.taskType = task.taskType;
  newTaskDTO.taskTypeId = task.taskTypeId;
  newTaskDTO.taskTypeFamilyId = task.taskTypeFamilyId;
  newTaskDTO.auditTrailId = task.auditTrailId;
  newTaskDTO.commentTrailId = task.commentTrailId;
  newTaskDTO.checkList = task.checkList.toJSON();
  newTaskDTO.name = task.name;
  newTaskDTO.description = task.description;
  newTaskDTO.startDateTime = toApiDateTimeRequired(task.startDateTime);
  newTaskDTO.endDateTime = toApiDateTimeRequired(task.endDateTime);
  newTaskDTO.duration = task.duration;
  newTaskDTO.userId = task.userId;
  newTaskDTO.ownerId = task.ownerId;
  newTaskDTO.recurringPattern = task.recurringPattern?.toJSON();
  newTaskDTO.recurringPatternSummary = task.recurringPatternSummary;
  newTaskDTO.deadline = toApiDateTimeOptional(task.deadline);
  newTaskDTO.eventId = task.eventId;
  newTaskDTO.controlIds = task.controlIds ? new IdListDTO(task.controlIds.map((id) => id.toString())) : undefined;
  newTaskDTO.processIds = task.processIds ? new IdListDTO(task.processIds.map((id) => id.toString())) : undefined;
  newTaskDTO.objectiveIds = task.objectiveIds ? new IdListDTO(task.objectiveIds.map((id) => id.toString())) : undefined;
  newTaskDTO.assetIds = task.assetIds ? new IdListDTO(task.assetIds.map((id) => id.toString())) : undefined;
  newTaskDTO.riskIds = task.riskIds ? new IdListDTO(task.riskIds.map((id) => id.toString())) : undefined;
  newTaskDTO.processingActivityIds = task.processingActivityIds
    ? new IdListDTO(task.processingActivityIds.map((id) => id.toString()))
    : undefined;
  newTaskDTO.tagIds = task.tagIds ? new IdListDTO(task.tagIds.map((id) => id.toString())) : undefined;
  newTaskDTO.resourceLinkIds = task.resourceLinkIds
    ? new IdListDTO(task.resourceLinkIds.map((id) => id.toString()))
    : undefined;
  if (options?.includeTaskRelations) {
    newTaskDTO.taskRelations = mapToTaskRelations(
      task.taskRelations.filter((relation) => shouldIncludeTaskRelationForSync(task, relation, options)),
      task.taskId,
    );
  }
  newTaskDTO.kpiData = mapToKPIData(task.kpiData);
  newTaskDTO.instances = uploadInstances(task);
  newTaskDTO.instancesEnabled = task.instancesEnabled;
  newTaskDTO.approved =
    task.approved === ApprovalState.Approved ? true : task.approved === ApprovalState.Rejected ? false : undefined;
  newTaskDTO.hidden = task.hidden;
  newTaskDTO.webhookStatus = task.webhookStatus;
  newTaskDTO.templateId = task.templateId;
  newTaskDTO.externalId = task.externalId;

  return newTaskDTO;
}

function uploadInstances(task: Task): TaskDTO[] | undefined {
  if (task.taskType === TaskTypes.Event || task.taskType === TaskTypes.EventTemplate) {
    //only upload for event types that can have actual subtasks
    //other types can have a recurring series but these instances can never be tasked by the back-end
    //because they are automatically generated by the back-end
    //
    //undefined means "no opinion": the back-end replaces the subtask set from this list and deletes
    //everything missing from it, so a task whose subtasks were never loaded (only SingleTask loads
    //them, with apiGetTaskInstances) must leave the field out instead of posting an empty list.
    //An empty array IS an opinion and still clears the subtasks. See mapFromTask for the other half.
    return task.instances ? mapToTasksArray(task.instances) : undefined;
  } else {
    return [];
  }
}

export function mapFromTasksArray(taskDTOs: TaskDTO[] | undefined, globalDataCache: GlobalDataCache): Task[] {
  if (taskDTOs === undefined || taskDTOs === null) {
    return [];
  }

  const taskList: Task[] = [];

  for (const taskDTO of taskDTOs) {
    const newTask = mapFromTask(taskDTO, globalDataCache);
    taskList.push(newTask);
  }

  return taskList;
}

export function mapToTasksArray(tasks: Task[] | undefined, options?: ITaskMappingOptions): TaskDTO[] {
  if (tasks === undefined) {
    return [];
  }

  const taskList: TaskDTO[] = [];

  for (const task of tasks) {
    const newTask = mapToTask(task, options);
    taskList.push(newTask);
  }

  return taskList;
}

export function mapFromTasks(tasksDTO: TasksDTO, globalDataCache: GlobalDataCache): Tasks {
  const taskCol = new Tasks();

  taskCol.tasks = mapFromTasksArray(tasksDTO.tasks, globalDataCache);
  taskCol.tasks.forEach((task) => (task.taskStates = globalDataCache.taskStates.items));
  taskCol.errorInfo = mapFromApiErrorInfoDictionary(tasksDTO.errorInfo);

  return taskCol;
}

export function mapFromTask(taskDTO: TaskDTO, globalDataCache: GlobalDataCache): Task {
  const newTask: Task = new Task();

  newTask.taskId = taskDTO.taskId;
  newTask.authSchemaId = taskDTO.authSchemaId;
  newTask.taskStateId = taskDTO.taskStateId;
  newTask.taskStates = [...globalDataCache.taskStates.items];
  newTask.sortOrder = taskDTO.sortOrder;
  newTask.creatorId = taskDTO.creatorId;
  newTask.created = fromApiDateTimeRequired(taskDTO.created);
  newTask.latestChangeDateTime = fromApiDateTimeOptional(taskDTO.latestChangeDateTime);
  newTask.completed = fromApiDateTimeOptional(taskDTO.completed);
  newTask.creator = globalDataCache.users.get(taskDTO.creatorId);
  newTask.taskMasterId = taskDTO.taskMasterId;
  newTask.taskType = taskDTO.taskType;
  newTask.taskTypeId = taskDTO.taskTypeId;
  newTask.taskTypeFamilyId = taskDTO.taskTypeFamilyId;
  newTask.formVersion = taskDTO.formVersion ? mapFromTaskType(taskDTO.formVersion) : undefined;
  newTask.auditTrailId = taskDTO.auditTrailId;
  newTask.commentTrailId = taskDTO.commentTrailId;
  newTask.commentCount = taskDTO.commentCount;
  newTask.checkList = new TaskCheckList();
  newTask.name = taskDTO.name;
  newTask.description = taskDTO.description;
  newTask.startDateTime = fromApiDateTimeRequired(taskDTO.startDateTime);
  newTask.endDateTime = fromApiDateTimeRequired(taskDTO.endDateTime);
  newTask.duration = taskDTO.duration;
  newTask.hasFollowUp = taskDTO.hasFollowUp ?? false;
  newTask.followUpTotal = taskDTO.followUpTotal ?? 0;
  newTask.followUpCompleted = taskDTO.followUpCompleted ?? 0;
  newTask.hasUnlinkedFollowUp = taskDTO.hasUnlinkedFollowUp ?? false;

  if (taskDTO.userId) {
    const assignee = globalDataCache.users.get(taskDTO.userId);
    if (assignee.hasLicense) {
      newTask.userId = taskDTO.userId;
      newTask.user = assignee;
    }
  }

  if (taskDTO.ownerId) {
    const owner = globalDataCache.users.get(taskDTO.ownerId);
    if (owner.hasLicense) {
      newTask.ownerId = taskDTO.ownerId;
      newTask.owner = owner;
    }
  }

  //A task must have an owner. When the owner is not licensed anymore or deleted, fallback to system user
  if (!newTask.ownerId) {
    newTask.ownerId = newGuidNil();
  }

  //deadline on the DTO is optional because it depends on whether the Recurrence pattern is active. if this is a recurring task, set the deadline start + 14 days
  newTask.deadline = fromApiDateTimeOptional(taskDTO.deadline) || addDateTimeDays(newTask.startDateTime, 14);
  newTask.eventId = taskDTO.eventId;
  newTask.controlIds = taskDTO.controlIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newTask.processIds = taskDTO.processIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newTask.objectiveIds = taskDTO.objectiveIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newTask.assetIds = taskDTO.assetIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newTask.riskIds = taskDTO.riskIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newTask.normIds = taskDTO.normIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newTask.processingActivityIds = taskDTO.processingActivityIds?.idList?.map<number>((i) => Number(i)) ?? [];
  //absence is preserved as undefined instead of an empty list: the back-end never returns subtasks
  //with a task, so an empty list here would be indistinguishable from "this event has no subtasks"
  //and uploadInstances would post it back as the whole truth. See uploadInstances for the other half.
  newTask.instances = taskDTO.instances ? mapFromTasksArray(taskDTO.instances, globalDataCache) : undefined;
  newTask.instancesEnabled = taskDTO.instancesEnabled;
  newTask.approved =
    taskDTO.approved === true
      ? ApprovalState.Approved
      : taskDTO.approved === false
      ? ApprovalState.Rejected
      : ApprovalState.Pending;
  newTask.hidden = taskDTO.hidden;
  newTask.webhookStatus = taskDTO.webhookStatus;
  newTask.templateId = taskDTO.templateId;
  newTask.internalAuditId = taskDTO.internalAuditId;
  newTask.tagIds = taskDTO.tagIds?.idList?.map<number>((i) => Number(i));
  newTask.resourceLinkIds = taskDTO.resourceLinkIds?.idList?.map<number>((i) => Number(i));
  newTask.auditTrail = taskDTO.auditTrail ? mapFromAuditTrail(taskDTO.auditTrail) : undefined;
  newTask.taskRelations = taskDTO.taskRelations
    ? taskDTO.taskRelations.map((r) => mapFromTaskRelation(r, globalDataCache))
    : [];
  newTask.systemTaskType =
    newTask.formVersion?.systemTaskType ??
    globalDataCache.taskTypes.getSystemTaskType(taskDTO.taskTypeId) ??
    SystemTaskTypes.None;
  newTask.taskKind = taskDTO.taskKind ?? TaskKinds.None;
  newTask.externalId = taskDTO.externalId;

  //parse the Recurrence pattern
  newTask.recurringPattern = new RecurringPattern();
  newTask.recurringPattern.fromJSON(taskDTO.recurringPattern, newTask.startDateTime);
  newTask.recurringPatternSummary = taskDTO.recurringPatternSummary;

  //parse the checkList
  newTask.checkList.fromJSON(taskDTO.checkList);

  return newTask;
}

export function mapFromTaskState(taskStateDTO: TaskStateDTO): TaskState {
  const taskState = new TaskState();
  taskState.taskStateId = taskStateDTO.taskStateId;
  taskState.completed = taskStateDTO.completed;
  taskState.sortOrder = taskStateDTO.sortOrder;
  taskState.taskCount = taskStateDTO.taskCount;
  taskState.trans = taskStateDTO.trans.map((taskState_TranslationDTO: TaskState_TranslationDTO) => {
    return mapFromTaskState_Translation(taskState_TranslationDTO);
  });

  if (taskState.trans && taskState.trans.length > 0) {
    taskState.transIdx = 0;
    taskState.state = taskState.trans[0].state;
  }

  return taskState;
}

export function mapFromTaskStates(taskStateDTO: TaskStateDTO[]): TaskState[] {
  return taskStateDTO.map((_stateDTO) => {
    return mapFromTaskState(_stateDTO);
  });
}

export function mapFromTaskState_Translation(
  taskState_TranslationDTO: TaskState_TranslationDTO,
): TaskState_Translation {
  const taskState_Translation = new TaskState_Translation();
  taskState_Translation.state = taskState_TranslationDTO.state;
  taskState_Translation.lang = mapFromLanguage(taskState_TranslationDTO.lang);

  return taskState_Translation;
}

export function mapFromTaskTemplateScene(
  taskTemplateSceneDTO: TaskTemplateSceneDTO,
  globalDataCache: GlobalDataCache,
): TaskTemplateScene {
  const taskTemplateScene = new TaskTemplateScene();

  const taskList: Task[] = [];

  if (taskTemplateSceneDTO.tasks) {
    for (const taskDTO of taskTemplateSceneDTO.tasks) {
      const newTask = mapFromTask(taskDTO, globalDataCache);
      taskList.push(newTask);
    }
  }

  taskTemplateScene.tasks = taskList;

  for (const task of taskTemplateScene.tasks) {
    const stat = taskTemplateSceneDTO.templateStats[task.taskId];
    if (stat) {
      const newStat = new TemplateStats();
      newStat.count = stat.count;
      newStat.lastUsed = fromApiDateTimeRequired(stat.lastUsed);
      taskTemplateScene.templateStats[task.taskId] = newStat;
    }
  }

  return taskTemplateScene;
}

export function mapFromDateRange(dateRangeDTO: DateRangeDTO): DateRange {
  const output = new DateRange();

  output.start = fromApiDateOptional(dateRangeDTO.start);
  output.end = fromApiDateOptional(dateRangeDTO.end);

  return output;
}

//task context
export const mapFromTaskContext = (context: TaskContextDTO, appContext: IAppContext): TaskContext => {
  const output = new TaskContext();
  output.controls = mapFromControls(context.controls, appContext.globalDataCache);
  output.themes = mapFromThemes(context.themes, appContext.globalDataCache);
  output.risks = mapFromRisksArray(context.risks, appContext);
  output.assets = mapFromAssets(context.assets, appContext.globalDataCache);
  output.processes = mapFromProcesses(context.processes, appContext.globalDataCache);
  output.objectives = mapFromObjectives(context.objectives, appContext.globalDataCache);
  output.taskRelations = context.taskRelations
    ? context.taskRelations.map((r) => mapFromTaskRelation(r, appContext.globalDataCache))
    : [];
  output.processingActivities = mapFromProcessingActivities(context.processingActivities, appContext.globalDataCache);

  return output;
};

export const mapToTaskRelations = (relations: TaskRelation[], sourceTaskId?: number): TaskRelationDTO[] => {
  return relations.map((r) => mapToTaskRelation(r, sourceTaskId));
};

const shouldIncludeTaskRelationForSync = (
  task: Task,
  relation: TaskRelation,
  options?: ITaskMappingOptions,
): boolean => {
  if (shouldIncludeRelationForNewTaskSync(task.taskId, relation.taskIdFrom)) return true;
  if (relation.taskIdFrom === task.taskId) return true;

  return (
    relation.taskTaskId > 0 &&
    options?.originalTaskRelations?.some(
      (originalRelation) =>
        originalRelation.taskTaskId === relation.taskTaskId && originalRelation.taskIdFrom === task.taskId,
    ) === true
  );
};

export const mapToTaskRelation = (relation: TaskRelation, sourceTaskId?: number): TaskRelationDTO => {
  const output = new TaskRelationDTO();
  output.taskTaskId = relation.taskTaskId;
  output.taskIdFrom = mapRelationSourceIdForTaskSync(relation.taskIdFrom, sourceTaskId);
  output.taskIdTo = relation.taskIdTo;
  output.relationship = relation.relationship;
  output.notifyUserId = relation.notifyUserId;

  return output;
};

export const mapFromTaskRelation = (dto: TaskRelationDTO, globalCache: GlobalDataCache): TaskRelation => {
  const output = new TaskRelation(dto.taskIdFrom, dto.taskIdTo ?? null, dto.relationship);
  output.taskTaskId = dto.taskTaskId;
  output.notifyUserId = dto.notifyUserId;
  output.taskFrom = dto.taskFrom ? mapFromTask(dto.taskFrom, globalCache) : undefined;
  output.taskTo = dto.taskTo ? mapFromTask(dto.taskTo, globalCache) : undefined;

  return output;
};

//task checklist conversion
export const mapFromTaskCheckListConversion = (
  taskCheckListConversionDTO: TaskCheckListConversionDTO,
): TaskCheckListConversion => {
  const output = new TaskCheckListConversion();
  output.taskId = taskCheckListConversionDTO.taskId;
  output.taskType = taskCheckListConversionDTO.taskType
    ? mapFromTaskType(taskCheckListConversionDTO.taskType)
    : undefined;

  return output;
};
